import { useState, useMemo, useRef } from 'react';
import type { ManifestBarcode, ScanFlag, ScanEvent } from '@/types/manifest';
import { deleteBarcode, getScansByBarcodeValue, deleteScan, restoreBarcode, unflagBarcode, markBarcodesScanned } from '../api/manifests-api';
import { useUndoHistory } from '@/contexts/undo-history-context';
import { formatToESTShort } from '@/lib/utils/date-format';
import { useDialog } from '@/hooks/useDialog';
import { Dialog } from '@/components/ui/dialog';
import { inventoryApi } from '@/lib/api/inventory';
import type { BulkSettableStatus } from '@/types/inventory';
import { BulkSetStatusControl } from '@/features/inventory/components/bulk-set-status-control';
import { InventoryStatusBadge } from '@/features/inventory/components/inventory-status-badge';

interface BarcodeListProps {
  manifestId: number;
  manifestName?: string;
  barcodes: ManifestBarcode[];
  onDeleted: () => void;
  canDelete: boolean;
  onViewBarcode?: (value: string) => void;
  onFlagToggle?: (barcodeId: number, currentlyFlagged: boolean) => void;
  /** Admin-only: show the bulk "Set Status" control in the selection bar. */
  canSetStatus?: boolean;
}

type FilterOption = 'all' | 'pending' | 'scanned' | 'flagged' | 'manual' | 'duplicates';

const flagStyles: Record<ScanFlag, string> = {
  None: '',
  Duplicate: 'bg-yellow-500/20 text-yellow-400',
  Unmanifested: 'bg-red-500/20 text-red-400',
  Manual: 'bg-purple-500/20 text-purple-400',
};

function matchesFilter(
  b: ManifestBarcode,
  filter: FilterOption,
  search: string,
  duplicateBarcodes: Set<string>
): boolean {
  if (search && !b.barcodeValue.toLowerCase().includes(search.toLowerCase())) {
    return false;
  }

  switch (filter) {
    case 'pending':
      return !b.isScanned;
    // Both key off isFlagged, the live state the row's red styling and the release gate use.
    // Keying off `flag` instead missed rows an admin flagged by hand (no scan event, so `flag`
    // comes back null) and, before ScanFlag.Manual stopped quarantining items, wrongly pushed
    // every hand-keyed entry out of Scanned and into Flagged.
    case 'scanned':
      return b.isScanned && !b.isFlagged;
    case 'flagged':
      return b.isFlagged;
    // Keyed in by hand, whether or not it is also an exception. Manual entries are not flagged,
    // so the Flagged filter cannot surface them and there was previously no way to list them.
    case 'manual':
      return b.scanType === 'Manual';
    case 'duplicates':
      return duplicateBarcodes.has(b.barcodeValue);
    default:
      return true;
  }
}

export function BarcodeList({ manifestId, manifestName, barcodes, onDeleted, canDelete, onViewBarcode, onFlagToggle, canSetStatus = false }: BarcodeListProps) {
  const { push } = useUndoHistory();
  const [filter, setFilter] = useState<FilterOption>('all');
  const [search, setSearch] = useState('');
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [expandedBarcodes, setExpandedBarcodes] = useState<Set<number>>(new Set());
  const [loadingScans, setLoadingScans] = useState<Set<string>>(new Set());
  const [scansData, setScansData] = useState<Map<string, ScanEvent[]>>(new Map());
  const [deletingScanId, setDeletingScanId] = useState<number | null>(null);
  const [selectedItems, setSelectedItems] = useState<Set<number>>(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkReleasing, setBulkReleasing] = useState(false);
  const [bulkStatus, setBulkStatus] = useState<BulkSettableStatus | ''>('');
  const [bulkSettingStatus, setBulkSettingStatus] = useState(false);
  const [bulkMarkingScanned, setBulkMarkingScanned] = useState(false);
  // Bulk delete, release, set-status and mark-scanned are mutually exclusive: one at a time. The
  // ref is the synchronous guard (state alone cannot stop a second click landing before
  // re-render); the state drives the disabled buttons.
  const bulkInFlightRef = useRef(false);
  const bulkBusy = bulkDeleting || bulkReleasing || bulkSettingStatus || bulkMarkingScanned;
  const { dialog, closeDialog, showConfirm, showSuccess, showWarning, showError } = useDialog();

  // Detect duplicate barcodes
  const duplicateBarcodes = useMemo(() => {
    const counts = new Map<string, number>();
    barcodes.forEach(b => counts.set(b.barcodeValue, (counts.get(b.barcodeValue) || 0) + 1));
    return new Set(
      Array.from(counts.entries())
        .filter(([_, count]) => count > 1)
        .map(([value, _]) => value)
    );
  }, [barcodes]);

  const filteredBarcodes = useMemo(
    () =>
      barcodes
        .filter((b) => matchesFilter(b, filter, search, duplicateBarcodes))
        .sort((a, b) => a.barcodeValue.localeCompare(b.barcodeValue) || a.id - b.id),
    [barcodes, filter, search, duplicateBarcodes]
  );

  // Bulk actions only ever act on rows the user can see. Changing the filter or search prunes the
  // selection to still-visible rows (so hidden rows are never deleted, released or re-statused
  // unseen), and this derived list also drops selected rows a background refresh moved out of view.
  const selectedIds = useMemo(
    () => filteredBarcodes.filter((b) => selectedItems.has(b.id)).map((b) => b.id),
    [filteredBarcodes, selectedItems]
  );

  const applyFilterChange = (nextFilter: FilterOption, nextSearch: string) => {
    setFilter(nextFilter);
    setSearch(nextSearch);
    setSelectedItems((prev) => {
      if (prev.size === 0) return prev;
      const visible = new Set(
        barcodes.filter((b) => matchesFilter(b, nextFilter, nextSearch, duplicateBarcodes)).map((b) => b.id)
      );
      const pruned = new Set(Array.from(prev).filter((id) => visible.has(id)));
      return pruned.size === prev.size ? prev : pruned;
    });
  };

  const duplicateCount = duplicateBarcodes.size;
  const manualCount = barcodes.filter((b) => b.scanType === 'Manual').length;

  const handleDelete = async (barcodeId: number) => {
    const barcode = barcodes.find((b) => b.id === barcodeId);
    showConfirm(
      'Delete Barcode',
      'Remove this barcode from the manifest? You can undo this from the history menu.',
      async () => {
        setDeletingId(barcodeId);
        try {
          const response = await deleteBarcode(manifestId, barcodeId);
          if (response.success) {
            push(
              barcode?.barcodeValue ?? `Barcode #${barcodeId}`,
              () => restoreBarcode(manifestId, barcodeId).then(() => {}),
              manifestName ? `Removed from ${manifestName}` : 'Removed from manifest',
              onDeleted
            );
            onDeleted();
          }
        } finally {
          setDeletingId(null);
        }
      },
      'Delete'
    );
  };

  const toggleItemSelection = (id: number) => {
    const newSelection = new Set(selectedItems);
    if (newSelection.has(id)) newSelection.delete(id);
    else newSelection.add(id);
    setSelectedItems(newSelection);
  };

  const handleBulkDelete = () => {
    if (selectedIds.length === 0 || bulkInFlightRef.current) return;
    const ids = selectedIds;
    const items = barcodes.filter((b) => ids.includes(b.id));
    showConfirm(
      'Delete Barcodes',
      `Remove ${ids.length} barcode(s) from the manifest? You can undo this from the history menu.`,
      async () => {
        if (bulkInFlightRef.current) return;
        bulkInFlightRef.current = true;
        setBulkDeleting(true);
        try {
          await Promise.all(ids.map((id) => deleteBarcode(manifestId, id)));
          const label =
            ids.length === 1
              ? items[0]?.barcodeValue ?? `Barcode #${ids[0]}`
              : `${ids.length} barcodes`;
          const sublabel = manifestName
            ? `Removed from ${manifestName}`
            : 'Removed from manifest';
          push(
            label,
            () => Promise.all(ids.map((id) => restoreBarcode(manifestId, id))).then(() => {}),
            sublabel,
            onDeleted
          );
          setSelectedItems(new Set());
          onDeleted();
        } finally {
          bulkInFlightRef.current = false;
          setBulkDeleting(false);
        }
      },
      'Delete'
    );
  };

  const flaggedSelectedIds = useMemo(
    () => filteredBarcodes.filter((b) => selectedItems.has(b.id) && b.isFlagged).map((b) => b.id),
    [filteredBarcodes, selectedItems]
  );

  const handleBulkRelease = async () => {
    if (flaggedSelectedIds.length === 0 || bulkInFlightRef.current) return;
    bulkInFlightRef.current = true;
    setBulkReleasing(true);
    try {
      await Promise.all(flaggedSelectedIds.map((id) => unflagBarcode(manifestId, id)));
      setSelectedItems(new Set());
      onDeleted();
    } finally {
      bulkInFlightRef.current = false;
      setBulkReleasing(false);
    }
  };

  const handleBulkSetStatus = () => {
    if (selectedIds.length === 0 || !bulkStatus || bulkInFlightRef.current) return;
    // ManifestBarcode ids are the inventory item ids the inventory API expects.
    const ids = selectedIds;
    const status = bulkStatus;
    showConfirm(
      'Set Status',
      `Set status to "${status}" for ${ids.length} barcode(s)?`,
      async () => {
        if (bulkInFlightRef.current) return;
        bulkInFlightRef.current = true;
        setBulkSettingStatus(true);
        try {
          const { message, skippedCount } = await inventoryApi.bulkSetStatus(ids, status);
          // Drop only what was sent; anything selected since the confirm stays selected.
          setSelectedItems((prev) => {
            const next = new Set(prev);
            ids.forEach((id) => next.delete(id));
            return next;
          });
          setBulkStatus('');
          onDeleted();
          // Coils on an order are skipped, not refused; the message names them.
          if (skippedCount > 0) showWarning('Status Partly Updated', message ?? `Some selected items were skipped because they are on an order`);
          else showSuccess('Status Updated', message || `Status set to "${status}" for ${ids.length} barcode(s)`);
        } catch (error) {
          // Show the server's refusal verbatim (it names the offending barcodes); keep the selection.
          const message =
            (error as { response?: { data?: { message?: string } } }).response?.data?.message ||
            'Failed to update status';
          showError('Could Not Set Status', message);
        } finally {
          bulkInFlightRef.current = false;
          setBulkSettingStatus(false);
        }
      },
      'Set Status'
    );
  };

  const unscannedSelectedIds = useMemo(
    () => filteredBarcodes.filter((b) => selectedItems.has(b.id) && !b.isScanned).map((b) => b.id),
    [filteredBarcodes, selectedItems]
  );

  const handleBulkMarkScanned = () => {
    if (unscannedSelectedIds.length === 0 || bulkInFlightRef.current) return;
    const ids = unscannedSelectedIds;
    showConfirm(
      'Mark as Scanned',
      `Mark ${ids.length} barcode(s) as scanned? They will be received under your name and go to Needs a Home to be put away.`,
      async () => {
        if (bulkInFlightRef.current) return;
        bulkInFlightRef.current = true;
        setBulkMarkingScanned(true);
        try {
          const response = await markBarcodesScanned(manifestId, ids);
          setSelectedItems((prev) => {
            const next = new Set(prev);
            ids.forEach((id) => next.delete(id));
            return next;
          });
          onDeleted();
          showSuccess('Marked as Scanned', response.message || `Marked ${ids.length} barcode(s) as scanned`);
        } catch (error) {
          const message =
            (error as { response?: { data?: { message?: string } } }).response?.data?.message ||
            'Failed to mark barcodes as scanned';
          showError('Could Not Mark as Scanned', message);
        } finally {
          bulkInFlightRef.current = false;
          setBulkMarkingScanned(false);
        }
      },
      'Mark Scanned'
    );
  };

  const toggleExpand = async (barcodeId: number, barcodeValue: string) => {
    const newExpanded = new Set(expandedBarcodes);
    if (newExpanded.has(barcodeId)) {
      newExpanded.delete(barcodeId);
      setExpandedBarcodes(newExpanded);
    } else {
      newExpanded.add(barcodeId);
      setExpandedBarcodes(newExpanded);

      // Fetch scans if not already loaded (scans are shared by value across duplicates)
      if (!scansData.has(barcodeValue)) {
        setLoadingScans(new Set(loadingScans).add(barcodeValue));
        try {
          const response = await getScansByBarcodeValue(manifestId, barcodeValue);
          if (response.success && response.data) {
            const newScansData = new Map(scansData);
            newScansData.set(barcodeValue, response.data);
            setScansData(newScansData);
          }
        } finally {
          const newLoading = new Set(loadingScans);
          newLoading.delete(barcodeValue);
          setLoadingScans(newLoading);
        }
      }
    }
  };

  const handleDeleteScan = async (scanId: number, barcodeValue: string) => {
    showConfirm(
      'Delete Scan',
      'Are you sure you want to delete this scan event? This action cannot be undone.',
      async () => {
        setDeletingScanId(scanId);
        try {
          const response = await deleteScan(manifestId, scanId);
          if (response.success) {
            // Refresh scans for this barcode
            const refreshResponse = await getScansByBarcodeValue(manifestId, barcodeValue);
            if (refreshResponse.success && refreshResponse.data) {
              const newScansData = new Map(scansData);
              newScansData.set(barcodeValue, refreshResponse.data);
              setScansData(newScansData);
            }
            // Refresh the whole list to update counts
            onDeleted();
          }
        } finally {
          setDeletingScanId(null);
        }
      },
      'Delete'
    );
  };


  const filterButtons: { value: FilterOption; label: string }[] = [
    { value: 'all', label: 'All' },
    { value: 'pending', label: 'Pending' },
    { value: 'scanned', label: 'Scanned' },
    { value: 'flagged', label: 'Flagged' },
    { value: 'manual', label: `Manual ${manualCount > 0 ? `(${manualCount})` : ''}` },
    { value: 'duplicates', label: `Duplicates ${duplicateCount > 0 ? `(${duplicateCount})` : ''}` },
  ];

  return (
    <div>
      <div className="flex flex-col sm:flex-row gap-4 mb-4">
        <input
          type="text"
          value={search}
          onChange={(e) => applyFilterChange(filter, e.target.value)}
          placeholder="Search barcodes..."
          className="flex-1 px-4 py-2 rounded bg-gray-700 border border-gray-600 focus:border-blue-500 focus:outline-none"
        />
        <div className="flex gap-2">
          {filterButtons.map(({ value, label }) => (
            <button
              key={value}
              onClick={() => applyFilterChange(value, search)}
              className={`px-3 py-2 text-sm rounded transition-colors ${
                filter === value
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {canDelete && selectedIds.length > 0 && (
        <div className="flex items-center gap-4 px-4 py-3 mb-4 rounded-lg bg-blue-900/30 border border-blue-700/50">
          <span className="text-sm text-blue-300 font-medium">{selectedIds.length} selected</span>
          {onFlagToggle && flaggedSelectedIds.length > 0 && (
            <button
              onClick={handleBulkRelease}
              disabled={bulkBusy}
              title="Unflag and release to Needs a Home"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded bg-emerald-600 hover:bg-emerald-500 text-white disabled:opacity-50 transition-colors"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
              </svg>
              {bulkReleasing
                ? 'Releasing...'
                : `Release ${flaggedSelectedIds.length} flagged`}
            </button>
          )}
          {canSetStatus && unscannedSelectedIds.length > 0 && (
            <button
              onClick={handleBulkMarkScanned}
              disabled={bulkBusy}
              title="Receive without a barge scan and send to Needs a Home"
              className="px-3 py-1.5 text-sm rounded bg-green-600 hover:bg-green-500 text-white disabled:opacity-50 transition-colors whitespace-nowrap"
            >
              {bulkMarkingScanned ? 'Marking...' : `Mark ${unscannedSelectedIds.length} Scanned`}
            </button>
          )}
          {canSetStatus && (
            <BulkSetStatusControl
              value={bulkStatus}
              onChange={setBulkStatus}
              onSubmit={handleBulkSetStatus}
              isSubmitting={bulkSettingStatus}
              disabled={bulkBusy}
            />
          )}
          <button
            onClick={handleBulkDelete}
            disabled={bulkBusy}
            className="px-3 py-1.5 text-sm rounded bg-red-600 hover:bg-red-500 text-white disabled:opacity-50 transition-colors"
          >
            {bulkDeleting ? 'Deleting...' : 'Delete Selected'}
          </button>
          <button
            onClick={() => setSelectedItems(new Set())}
            className="px-3 py-1.5 text-sm rounded text-gray-300 hover:text-white transition-colors"
          >
            Clear selection
          </button>
        </div>
      )}

      <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-700">
              {canDelete && (
                <th className="px-4 py-3 text-left">
                  <input
                    type="checkbox"
                    onChange={(e) => {
                      if (e.target.checked) setSelectedItems(new Set(filteredBarcodes.map((b) => b.id)));
                      else setSelectedItems(new Set());
                    }}
                    checked={filteredBarcodes.length > 0 && selectedIds.length === filteredBarcodes.length}
                  />
                </th>
              )}
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Barcode</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Status</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Scanned By</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Scanned At (EST)</th>
              {canDelete && (
                <th className="px-4 py-3 text-right text-sm font-medium text-gray-400">Actions</th>
              )}
            </tr>
          </thead>
          <tbody>
            {filteredBarcodes.length === 0 ? (
              <tr>
                <td colSpan={canDelete ? 6 : 4} className="px-4 py-8 text-center text-gray-400">
                  No barcodes found
                </td>
              </tr>
            ) : (
              filteredBarcodes.map((barcode) => {
                const isDuplicate = duplicateBarcodes.has(barcode.barcodeValue);
                const hasDuplicateFlag = barcode.flag === 'Duplicate';
                const isExpanded = expandedBarcodes.has(barcode.id);
                const scans = scansData.get(barcode.barcodeValue) || [];
                const isLoadingScans = loadingScans.has(barcode.barcodeValue);

                return (
                  <>
                    <tr
                      key={barcode.id}
                      className={`border-b border-gray-700 transition-colors ${
                        barcode.isFlagged
                          ? 'border-l-4 border-l-red-500 bg-red-950/20'
                          : isDuplicate || hasDuplicateFlag
                          ? 'bg-yellow-900/20'
                          : ''
                      }`}
                    >
                      {canDelete && (
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={selectedItems.has(barcode.id)}
                            onChange={() => toggleItemSelection(barcode.id)}
                          />
                        </td>
                      )}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => toggleExpand(barcode.id, barcode.barcodeValue)}
                            className="p-1 text-gray-400 hover:text-gray-300 transition-colors"
                            title={isExpanded ? "Collapse scans" : "Expand to see individual scans"}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              {isExpanded ? (
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              ) : (
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              )}
                            </svg>
                          </button>
                          {onViewBarcode && (
                            <button
                              onClick={() => onViewBarcode(barcode.barcodeValue)}
                              className="p-1 text-gray-400 hover:text-white transition-colors"
                              title="View barcode"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                              </svg>
                            </button>
                          )}
                          <span className="font-mono">{barcode.barcodeValue}</span>
                          {/* Entry method, shown alongside the flag rather than inside it. An
                              off-manifest type-in is Manual + Unmanifested, and the flag column can
                              only render one badge — without this the hand-keyed part was visible
                              only by expanding the row. */}
                          {barcode.scanType === 'Manual' && (
                            <span
                              className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-500/20 text-purple-400"
                              title="Entered manually"
                            >
                              TYPED
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap items-center gap-2">
                          {barcode.isFlagged ? (
                            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-red-900/60 border border-red-700/50 text-red-300 text-xs font-medium">
                              <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M3 3v18M3 4l9 4-9 4" />
                              </svg>
                              {barcode.flag && barcode.flag !== 'None' ? barcode.flag : 'Flagged'}
                            </span>
                          ) : barcode.isScanned ? (
                            barcode.flag && barcode.flag !== 'None' ? (
                              <span className={`px-2 py-1 rounded text-xs ${flagStyles[barcode.flag]}`}>
                                {barcode.flag}
                              </span>
                            ) : (
                              <span className="text-green-400">Scanned</span>
                            )
                          ) : (
                            <span className="text-gray-400">Pending</span>
                          )}
                          {/* Inventory lifecycle status, shown alongside the scan status so a bulk
                              Set Status is visible here. Absent on responses from an older API. */}
                          {barcode.status && (
                            <InventoryStatusBadge status={barcode.status} title="Inventory status" srLabel="Inventory status" />
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-300">{barcode.scannedBy || '-'}</td>
                      <td className="px-4 py-3 text-gray-300">
                        {formatToESTShort(barcode.scannedAt)}
                      </td>
                      {canDelete && (
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            {onFlagToggle && (
                              barcode.isFlagged ? (
                                <button
                                  onClick={() => onFlagToggle(barcode.id, true)}
                                  title="Unflag and release to Needs a Home"
                                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded bg-emerald-900/50 hover:bg-emerald-800/70 border border-emerald-700/50 text-emerald-400 hover:text-emerald-300 text-xs font-medium transition-all whitespace-nowrap"
                                >
                                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                                  </svg>
                                  Release
                                </button>
                              ) : (
                                <button
                                  onClick={() => onFlagToggle(barcode.id, false)}
                                  title="Flag this barcode"
                                  className="p-1 text-gray-500 hover:text-red-400 transition-colors"
                                >
                                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 3v18M3 4l9 4-9 4" />
                                  </svg>
                                </button>
                              )
                            )}
                            <button
                              onClick={() => handleDelete(barcode.id)}
                              disabled={deletingId === barcode.id}
                              className="text-red-400 hover:text-red-300 text-sm disabled:opacity-50"
                            >
                              {deletingId === barcode.id ? 'Deleting...' : 'Delete'}
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>

                    {/* Expanded scan details */}
                    {isExpanded && (
                      <tr key={`${barcode.id}-scans`} className="bg-gray-900/50">
                        <td colSpan={canDelete ? 6 : 4} className="px-4 py-3">
                          <div className="ml-8">
                            <p className="text-sm text-gray-400 mb-2 font-semibold">Individual Scans:</p>
                            {isLoadingScans ? (
                              <p className="text-sm text-gray-500">Loading scans...</p>
                            ) : scans.length === 0 ? (
                              <p className="text-sm text-gray-500">No scans found</p>
                            ) : (
                              <div className="space-y-1">
                                {scans.map((scan) => (
                                  <div key={scan.id} className="flex items-center justify-between py-2 px-3 bg-gray-800/50 rounded">
                                    <div className="flex items-center gap-4 text-sm">
                                      <span className="text-gray-400">
                                        {formatToESTShort(scan.timestamp)}
                                      </span>
                                      <span className="text-gray-300">by {scan.operatorName}</span>
                                      <span className={`px-2 py-0.5 rounded text-xs ${
                                        scan.flag && scan.flag !== 'None' ? flagStyles[scan.flag] : 'text-gray-500'
                                      }`}>
                                        {scan.scanType}
                                        {scan.flag && scan.flag !== 'None' && ` - ${scan.flag}`}
                                      </span>
                                    </div>
                                    {canDelete && (
                                      <button
                                        onClick={() => handleDeleteScan(scan.id, barcode.barcodeValue)}
                                        disabled={deletingScanId === scan.id}
                                        className="text-red-400 hover:text-red-300 text-xs disabled:opacity-50"
                                      >
                                        {deletingScanId === scan.id ? 'Deleting...' : 'Delete Scan'}
                                      </button>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <p className="mt-2 text-sm text-gray-400">
        Showing {filteredBarcodes.length} of {barcodes.length} barcodes
      </p>

      <Dialog
        isOpen={dialog.isOpen}
        onClose={closeDialog}
        title={dialog.title}
        message={dialog.message}
        type={dialog.type}
        confirmText={dialog.confirmText}
        cancelText={dialog.cancelText}
        onConfirm={dialog.onConfirm}
        showCancel={dialog.showCancel}
      />
    </div>
  );
}
